import { type EventRuleDto } from "@crowdcoursing/api/openapi";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useState } from "react";

// User-facing strings
const STRINGS = {
  AGREE_TO_RULES: "Please agree to all rules to continue",
  CANCEL: "Cancel",
  EVENT_RULES: "Event Rules",
  I_AGREE: "I agree",
  JOIN: "Join",
  REQUEST_JOIN: "Request to Join",
} as const;

export interface EventRulesAgreementModalProps {
  open: boolean;
  onClose: () => void;
  onJoin: () => void;
  rules: EventRuleDto[];
  isPrivate?: boolean;
}

export const EventRulesAgreementModal = ({
  open,
  onClose,
  onJoin,
  rules,
  isPrivate,
}: EventRulesAgreementModalProps) => {
  const [agreedRules, setAgreedRules] = useState<Record<string, boolean>>({});

  const handleAgreeToRule = (ruleName: string) => {
    setAgreedRules((prev) => ({
      ...prev,
      [ruleName]: !prev[ruleName],
    }));
  };

  const allRulesAgreed = rules.every((rule) => agreedRules[rule.name ?? ""]);

  const handleClose = () => {
    onClose();
    // Reset agreements when modal closes
    setAgreedRules({});
  };

  const handleJoin = () => {
    if (allRulesAgreed) {
      onJoin();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{STRINGS.EVENT_RULES}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography color="textSecondary">{STRINGS.AGREE_TO_RULES}</Typography>
        </Box>
        <List>
          {rules.map((rule) => (
            <ListItem key={rule.name} disablePadding>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedRules[rule.name ?? ""] ?? false}
                    onChange={() => handleAgreeToRule(rule.name ?? "")}
                  />
                }
                label={
                  <ListItemText
                    primary={rule.name}
                    secondary={rule.description}
                    slotProps={{
                      primary: { fontWeight: "bold" },
                    }}
                  />
                }
                sx={{ m: 0, py: 1, width: "100%" }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{STRINGS.CANCEL}</Button>
        <Button onClick={handleJoin} variant="contained" disabled={!allRulesAgreed}>
          {isPrivate ? STRINGS.REQUEST_JOIN : STRINGS.JOIN}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
